import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { dashboardAPI, authAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Calendar, Users, Euro, Clock, MapPin, Video,
  Bell, CheckCircle, Timer, AlertTriangle, RefreshCw,
  Scissors, TrendingUp, Activity, Stethoscope, ChevronRight,
  ShieldCheck, KeyRound
} from 'lucide-react';
import { format, differenceInMinutes, isAfter, isBefore, addMinutes } from 'date-fns';
import { es } from 'date-fns/locale';

import PinDialog from '@/components/common/PinDialog';
import { toast } from 'sonner';

function formatCurrency(value) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value || 0);
}

export default function DoctorDashboardPage() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [nextAppointment, setNextAppointment] = useState(null);
  const [pinStatus, setPinStatus] = useState({ has_pin: false });
  const [showPinSetup, setShowPinSetup] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const res = await dashboardAPI.getDoctorStats();
      setData(res.data);

      // Find next upcoming appointment
      const now = new Date();
      const nextApt = (res.data.today_appointments || []).find(apt => {
        const [h, m] = apt.start_time.split(':').map(Number);
        const aptTime = new Date(now);
        aptTime.setHours(h, m, 0, 0);
        return isAfter(aptTime, now) && apt.status === 'confirmed';
      });
      setNextAppointment(nextApt || null);
    } catch (error) {
      console.error('Error loading doctor dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadPinStatus();
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    const dataInterval = setInterval(loadData, 60000);
    return () => { clearInterval(timeInterval); clearInterval(dataInterval); };
  }, [loadData]);

  const loadPinStatus = async () => {
    try {
      const response = await authAPI.pinStatus();
      setPinStatus(response.data);
    } catch { /* ignore */ }
  };

  const handlePinSetupComplete = () => {
    setShowPinSetup(false);
    setPinStatus({ has_pin: true });
    toast.success('PIN de firma configurado correctamente');
  };

  const getMinutesUntil = (apt) => {
    const now = new Date();
    const [h, m] = apt.start_time.split(':').map(Number);
    const aptTime = new Date(now);
    aptTime.setHours(h, m, 0, 0);
    return differenceInMinutes(aptTime, now);
  };

  const formatCountdown = (apt) => {
    const mins = getMinutesUntil(apt);
    if (mins < 0) return 'Ya pasó';
    const hours = Math.floor(mins / 60);
    const remainder = mins % 60;
    return hours > 0 ? `${hours}h ${remainder}m` : `${remainder} min`;
  };

  const getAptStatus = (apt) => {
    const mins = getMinutesUntil(apt);
    const [sh, sm] = apt.start_time.split(':').map(Number);
    const [eh, em] = apt.end_time.split(':').map(Number);
    const now = new Date();
    const start = new Date(now); start.setHours(sh, sm, 0, 0);
    const end = new Date(now); end.setHours(eh, em, 0, 0);

    if (apt.status === 'completed') return { label: 'Completada', color: 'bg-brand-soft/60 text-brand-navy', icon: CheckCircle };
    if (apt.status === 'pending') return { label: 'Pendiente', color: 'bg-brand-soft text-brand-deep', icon: Bell };
    if (isAfter(now, end)) return { label: 'Finalizada', color: 'bg-gray-100 text-gray-600', icon: CheckCircle };
    if (isAfter(now, start) && isBefore(now, end)) return { label: 'En curso', color: 'bg-green-100 text-green-700 animate-pulse', icon: Timer };
    if (mins <= 15 && mins > 0) return { label: `En ${mins} min`, color: 'bg-red-100 text-red-700 animate-pulse', icon: AlertTriangle };
    if (mins <= 30 && mins > 0) return { label: `En ${mins} min`, color: 'bg-orange-100 text-orange-700', icon: Clock };
    return { label: apt.start_time, color: 'bg-gray-100 text-gray-600', icon: Clock };
  };

  const getDayProgress = () => {
    const now = new Date();
    const s = new Date(now); s.setHours(8, 0, 0, 0);
    const e = new Date(now); e.setHours(20, 0, 0, 0);
    if (isBefore(now, s)) return 0;
    if (isAfter(now, e)) return 100;
    return Math.round((differenceInMinutes(now, s) / differenceInMinutes(e, s)) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-3">
          <Stethoscope className="w-10 h-10 mx-auto text-emerald-500 animate-pulse" />
          <p className="text-gray-500">Cargando tu dashboard...</p>
        </div>
      </div>
    );
  }

  const todayApts = data?.today_appointments || [];
  const completedToday = data?.today_completed || 0;

  return (
    <div className="space-y-6" data-testid="doctor-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-navy flex items-center gap-2" data-testid="doctor-dashboard-title">
            <Stethoscope className="w-7 h-7 text-emerald-500" />
            Mi Panel
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {format(currentTime, "EEEE d 'de' MMMM, yyyy", { locale: es })} — {format(currentTime, 'HH:mm:ss')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-dashboard">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500" data-testid="kpi-today">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hoy</p>
                <p className="text-3xl font-bold text-brand-navy mt-1">{data?.today_appointments_count || 0}</p>
                <p className="text-xs text-gray-400 mt-0.5">{completedToday} completadas</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500" data-testid="kpi-pending">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pendientes</p>
                <p className="text-3xl font-bold text-brand-navy mt-1">{data?.pending_confirmations || 0}</p>
                <p className="text-xs text-gray-400 mt-0.5">por confirmar</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-brand-soft/40 flex items-center justify-center">
                <Bell className="w-6 h-6 text-brand-deep" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500" data-testid="kpi-month-appointments">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Este Mes</p>
                <p className="text-3xl font-bold text-brand-navy mt-1">{data?.month_appointments || 0}</p>
                <p className="text-xs text-gray-400 mt-0.5">citas realizadas</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-brand-soft/30 flex items-center justify-center">
                <Users className="w-6 h-6 text-brand-navy" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-violet-500" data-testid="kpi-revenue">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Facturación</p>
                <p className="text-2xl font-bold text-brand-navy mt-1">{formatCurrency(data?.month_revenue)}</p>
                <p className="text-xs text-gray-400 mt-0.5">este mes</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-brand-deep/10 flex items-center justify-center">
                <Euro className="w-6 h-6 text-brand-deep" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Day Progress + Next Appointment */}
      <Card className="border-2 border-emerald-200/60 bg-gradient-to-r from-emerald-50/40 to-teal-50/30" data-testid="realtime-panel">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-brand-navy text-lg">
              <Timer className="w-5 h-5 text-emerald-500" />
              Mi Jornada — Hoy
            </CardTitle>
            <Badge className="bg-emerald-600 text-white">
              {completedToday}/{todayApts.length} completadas
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Day Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Progreso del día (8:00 - 20:00)</span>
              <span className="text-brand-navy font-medium">{getDayProgress()}%</span>
            </div>
            <Progress value={getDayProgress()} className="h-2" />
          </div>

          {/* Next Appointment */}
          {nextAppointment && (
            <div className="bg-gradient-to-r from-emerald-100/70 to-teal-100/50 rounded-xl p-4 border border-emerald-300/40">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Bell className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Próxima cita en</p>
                    <p className="text-2xl font-bold text-brand-navy">{formatCountdown(nextAppointment)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-brand-navy">{nextAppointment.patient_name}</p>
                  <p className="text-sm text-gray-600">{nextAppointment.start_time} - {nextAppointment.end_time}</p>
                  <Badge className="mt-1 bg-emerald-100 text-emerald-700">
                    {nextAppointment.location_name?.substring(0, 25)}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Today's Timeline */}
          {todayApts.length === 0 ? (
            <div className="text-center py-6 bg-white/60 rounded-xl">
              <Calendar className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No hay citas programadas para hoy</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {todayApts.map((apt) => {
                const status = getAptStatus(apt);
                const StatusIcon = status.icon;
                return (
                  <div
                    key={apt.id}
                    className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                      status.label === 'En curso'
                        ? 'bg-green-50 border-green-300 shadow-md'
                        : status.label.includes('min')
                          ? 'bg-orange-50 border-orange-200'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                    data-testid={`today-apt-${apt.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col items-center min-w-[55px]">
                        <span className="text-lg font-bold text-brand-navy">{apt.start_time}</span>
                        <span className="text-xs text-gray-400">{apt.end_time}</span>
                      </div>
                      <div className="w-px h-10 bg-gray-200" />
                      <div>
                        <p className="font-medium text-brand-navy">{apt.patient_name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {apt.location_type === 'teleconsulta' ? <Video size={12} className="text-emerald-500" /> : <MapPin size={12} />}
                          <span>{apt.location_name?.substring(0, 25)}</span>
                        </div>
                      </div>
                    </div>
                    <Badge className={`flex items-center gap-1 ${status.color}`}>
                      <StatusIcon size={12} /> {status.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two-column: Upcoming appointments + Surgeries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Appointments */}
        <Card data-testid="upcoming-appointments">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-lg">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-brand-navy" />
                Próximas Citas
              </span>
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/appointments')} data-testid="goto-appointments">
                Ver todas <ChevronRight size={14} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.upcoming_appointments || []).length === 0 ? (
              <p className="text-center text-gray-400 py-4 text-sm">Sin citas próximas</p>
            ) : (
              <div className="space-y-2">
                {(data?.upcoming_appointments || []).map((apt) => (
                  <div key={apt.id} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="font-medium text-sm text-brand-navy">{apt.patient_name}</p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(apt.date + 'T00:00:00'), "EEE d MMM", { locale: es })} — {apt.start_time}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">{apt.location_name?.substring(0, 15)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Surgeries (shared) */}
        <Card data-testid="upcoming-surgeries">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-lg">
              <span className="flex items-center gap-2">
                <Scissors className="w-5 h-5 text-rose-500" />
                Cirugías Programadas
              </span>
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/surgeries')} data-testid="goto-surgeries">
                Ver todas <ChevronRight size={14} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.today_surgeries || []).length === 0 && (data?.upcoming_surgeries || []).length === 0 ? (
              <p className="text-center text-gray-400 py-4 text-sm">Sin cirugías próximas</p>
            ) : (
              <div className="space-y-2">
                {(data?.today_surgeries || []).map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg bg-rose-50 border border-rose-200">
                    <div>
                      <p className="font-medium text-sm text-brand-navy">{s.patient_name}</p>
                      <p className="text-xs text-gray-500">HOY — {s.start_time || 'Sin hora'}</p>
                    </div>
                    <Badge className="bg-rose-100 text-rose-700 text-xs">{s.location_name?.substring(0, 15)}</Badge>
                  </div>
                ))}
                {(data?.upcoming_surgeries || []).map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="font-medium text-sm text-brand-navy">{s.patient_name}</p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(s.date + 'T00:00:00'), "EEE d MMM", { locale: es })} — {s.start_time || 'Sin hora'}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">{s.location_name?.substring(0, 15)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend + By Location */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card data-testid="monthly-trend">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Tendencia Mensual
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.monthly_trend || []).length === 0 ? (
              <p className="text-center text-gray-400 py-4 text-sm">Sin datos suficientes</p>
            ) : (
              <div className="space-y-3">
                {(data?.monthly_trend || []).map((item) => {
                  const maxCount = Math.max(...(data?.monthly_trend || []).map(i => i.count), 1);
                  const pct = Math.round((item.count / maxCount) * 100);
                  const monthLabel = format(new Date(item._id + '-01'), 'MMM yyyy', { locale: es });
                  return (
                    <div key={item._id} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 capitalize">{monthLabel}</span>
                        <span className="text-brand-navy font-medium">{item.count} citas — {formatCurrency(item.revenue)}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="bg-emerald-500 rounded-full h-2 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* By Location */}
        <Card data-testid="by-location">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5 text-brand-navy" />
              Por Ubicación (este mes)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.by_location || []).length === 0 ? (
              <p className="text-center text-gray-400 py-4 text-sm">Sin datos este mes</p>
            ) : (
              <div className="space-y-3">
                {(data?.by_location || []).map((loc, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                    <div>
                      <p className="font-medium text-sm text-brand-navy">{loc._id || 'Sin ubicación'}</p>
                      <p className="text-xs text-gray-500">{loc.count} citas</p>
                    </div>
                    <p className="font-bold text-brand-navy">{formatCurrency(loc.revenue)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* PIN de Firma Section */}
      <Card className="border-2 border-emerald-200" data-testid="doctor-pin-card">
        <CardContent className="pt-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {pinStatus.has_pin ? (
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                </div>
              ) : (
                <div className="w-10 h-10 bg-brand-soft rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-brand-deep" />
                </div>
              )}
              <div>
                <p className="font-medium text-brand-navy">
                  PIN de Firma {pinStatus.has_pin ? 'Configurado' : 'No Configurado'}
                </p>
                <p className="text-sm text-gray-500">
                  {pinStatus.has_pin
                    ? 'Su PIN esta activo para firmar documentos'
                    : 'Configure su PIN para poder firmar documentos medicos'}
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowPinSetup(true)}
              variant={pinStatus.has_pin ? 'outline' : 'default'}
              className={pinStatus.has_pin ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
              data-testid="doctor-setup-pin-btn"
            >
              <KeyRound size={16} className="mr-2" />
              {pinStatus.has_pin ? 'Cambiar PIN' : 'Configurar PIN'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <PinDialog
        open={showPinSetup}
        onClose={() => setShowPinSetup(false)}
        mode="setup"
        onVerified={handlePinSetupComplete}
      />
    </div>
  );
}
